"use client";

import { useLocale } from "next-intl";
import { useQueryState } from "nuqs";
import { useCollection } from "@/features/collections/useCollections";
import { filterDefs } from "@/features/filters/filterDefs";
import { filterParams } from "@/features/filters/filterParams";
import { useSelectedFilters } from "@/features/filters/useSelectedFilters";
import { useIsBboxTracked } from "@/features/map/hooks/useIsBboxTracked";
import { useMapBbox } from "@/features/map/hooks/useMapBbox";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useSearchMasks } from "@/features/search/useSearchMasks";
import { repeated } from "@/lib/api";
import type { Bbox } from "@/features/map/utils/bbox";

/**
 * Everything that narrows the result set, in one place.
 *
 * The items list, the facet counts and the suggestions all have to agree on
 * this: a facet that says "12 in Beaujon" while the list is scoped differently
 * is worse than no facet at all. They each derive their query from this hook
 * rather than assembling their own, which is also what makes the shared parts of
 * their react-query keys line up.
 *
 * The one axis they part ways on is the viewport: only the list is scoped to the
 * map bounds. The filter counts and POI search deliberately drop the bbox and
 * run over the whole collection, since a value the map is currently hiding is
 * still one the user can pick.
 */
export interface ItemsScope {
  projectId: string | undefined;
  collectionId: string | undefined;
  lang: string;
  bbox: Bbox | null;
  search: string | undefined;
  filter: string[];
  filterLogic: "and" | "or";
  /**
   * The active tenant subset (`?subset=`), a named base filter resolved and
   * applied server-side. Passed straight through — the client only carries the
   * name; see the backend's `items.subset.ts`.
   */
  subset: string | undefined;
  /**
   * False while a viewport-scoped project is still waiting for the map to report
   * its bounds. Gates the query rather than describing it, so it stays out of
   * the cache key.
   */
  isReady: boolean;
}

export function useItemsScope(): ItemsScope {
  const configs = useProjectConfigs();
  const collection = useCollection();
  const lang = useLocale();
  const isBboxTracked = useIsBboxTracked();
  const bbox = useMapBbox((state) => state.bbox);
  const isBboxResolved = useMapBbox((state) => state.isResolved);
  const { selectedFilters } = useSelectedFilters();
  const { searchText } = useSearchMasks();
  const [subset] = useQueryState("subset");

  return {
    projectId: collection?.projectId,
    collectionId: collection?.id,
    lang,
    // A project that doesn't scope items to the viewport must not have the bbox
    // in its key, or panning would invalidate a cache entry that never changes.
    bbox: isBboxTracked ? bbox : null,
    search: searchText,
    filter: filterParams(selectedFilters, filterDefs(configs)),
    filterLogic: configs.filterLogic === "or" ? "or" : "and",
    subset: subset ?? undefined,
    // Holding the query back is what keeps a viewport-scoped project from
    // flashing its entire dataset before the map has said what's on screen.
    isReady: !isBboxTracked || isBboxResolved,
  };
}

/** The scope as request parameters, for `apiFetch`. */
export function scopeParams(scope: ItemsScope, filter = scope.filter) {
  return {
    bbox: scope.bbox ?? undefined,
    search: scope.search,
    filter: filter.length > 0 ? repeated(filter) : undefined,
    // Only sent when it changes the meaning of the filters, to keep the URL free
    // of a parameter that is almost always the default.
    filterLogic: filter.length > 0 && scope.filterLogic === "or" ? "or" : undefined,
    subset: scope.subset,
  };
}
